import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, Subscription, TimeoutError } from 'rxjs';
import { debounceTime, distinctUntilChanged, timeout } from 'rxjs/operators';
import { OFFLINE_COPY, ProjectDrawingsService } from '../../services/project-drawings.service';
import { DrawingRevisionDetail, DrawingSheet, DrawingSheetListParams } from '../../models/drawing-sheet';
import { MapFeatureLink } from '../../models/map-feature-link';
import { sheetStationText } from '../../helpers/design-context';
import { sheetPointFromClick } from '../../helpers/sheet-point';
import {
  anchorScrollDelta, bindSheetZoomListeners, buttonsReleased, capturePointer, clampZoom, DRAG_THRESHOLD_PX, DragTracker, isSafariGesture,
  pageFraction, releasePointer, SafariPinch, stepZoom, wheelZoomFactor,
} from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/sheet-zoom-pan';
import { SheetZoomControlsComponent } from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/sheet-zoom-controls/sheet-zoom-controls.component';

/** A point placed on a sheet revision's page image: x / y are 0-1 of the image (pdf.js default viewport, R19). */
export interface SheetPointPick {
  revision: DrawingRevisionDetail;
  sheet: DrawingSheet | null;
  point: { x: number; y: number };
}

/** Mark up asked on the sheet on screen: with no pin, the markup alone is the record's location (never an invented point) */
export interface SheetMarkUpTarget {
  revision: DrawingRevisionDetail;
  sheet: DrawingSheet | null;
}

const SHEET_PAGE_SIZE = 100;
/** Audit 1: a list request past this gives up with a friendly message and Retry */
export const SHEET_LIST_TIMEOUT_MS = 30_000;
/** Within this of the list's end, scrolling asks for the next page */
const LOAD_MORE_THRESHOLD_PX = 300;
const LIST_FAILED = 'Sheets could not be loaded. Try again.';
const LIST_TIMED_OUT = 'Sheets took too long to load. Check your connection and try again.';
const SEARCH_DEBOUNCE_MS = 300;
const ZOOMS = [1, 1.5, 2, 3];

/**
 * The design-context picker's Drawing tab (spec §12 slice 3): the project's current sheets (near the record's station
 * when it has one), the chosen revision's page image, and a pin where the user clicks. A record already linked to a
 * sheet opens on that revision and point, even when it is no longer current (spec §2).
 */
@Component({
  selector: 'app-design-context-sheet-picker',
  templateUrl: './design-context-sheet-picker.component.html',
  styleUrls: ['./design-context-sheet-picker.component.scss'],
  imports: [MatIconModule, MatProgressSpinnerModule, SheetZoomControlsComponent],
})
export class DesignContextSheetPickerComponent implements OnInit, OnDestroy {
  private drawings = inject(ProjectDrawingsService);
  private cdr = inject(ChangeDetectorRef);

  @Input() projectId: string;
  /** The record's station in feet, when known: sheets covering it are listed first. */
  @Input() station: number | null = null;
  /** The record's link: a sheet link opens on its revision and point. */
  @Input() link: MapFeatureLink | null = null;
  /** Offered once a point exists. */
  @Input() canMarkUp = false;
  /**
   * W51: strokes drawn on this sheet and held until the record saves (a strokes-only PNG of the whole page), drawn over
   * the page where the editor put them. Display only.
   */
  @Input() markupOverlay: string | null = null;
  /** The revision those strokes were drawn on: they show only over that page, never over another sheet being browsed */
  @Input() markupOverlayRevision: string | null = null;

  /** The held strokes, while the page on screen is the one they belong to */
  get showMarkupOverlay(): boolean {
    return !!this.markupOverlay && this.imageLoaded && !!this.revision && this.revision.uuid === this.markupOverlayRevision;
  }

  @Output() picked = new EventEmitter<SheetPointPick>();
  @Output() markUp = new EventEmitter<SheetMarkUpTarget>();
  /** Audit 2: Remove pin asked (the pin's menu, or Delete on the sheet); the dialog confirms when markup would go, then clearPoint() */
  @Output() removePin = new EventEmitter<void>();
  /** Audit 2: the record has markup on this sheet (held or saved): the toolbar says Edit markup */
  @Input() hasMarkup = false;
  /** The pin's menu (Remove pin), opened by a click on the pin */
  pinMenuOpen = false;

  sheets: DrawingSheet[] = [];
  loadingSheets = false;
  listError = '';
  /** Audit 1: the next page of the list (the whole register, 100 at a time) */
  loadingMore = false;
  moreError = '';
  private pageParams: DrawingSheetListParams | null = null;
  private nextStart = 0;
  private totalSheets = 0;
  query = '';
  /** True when the list is the station-filtered one. */
  nearStation = false;
  selectedSheetUuid: string | null = null;
  revision: DrawingRevisionDetail | null = null;
  loadingRevision = false;
  /** The page image has loaded; until then nothing can be placed (see sheetPointFromClick). */
  imageLoaded = false;
  revisionError = '';
  point: { x: number; y: number } | null = null;
  zoom = 1;

  private search = new Subject<string>();
  private subs = new Subscription();
  private listGeneration = 0;
  /** The query the list was last asked for */
  private loadedQuery: string | null = null;
  private revisionGeneration = 0;
  /** W75: zoom and pan as in the sheet viewer (sheet-zoom-pan.ts) */
  private readonly drag = new DragTracker();
  private dragPointer: number | null = null;
  private suppressClick = false;
  private readonly safariPinch = new SafariPinch();
  private unbindZoom: (() => void) | null = null;
  private scrollEl: HTMLElement | null = null;
  /** W75: the pin being dragged: its pointer, the grab's offset from the pin's tip, and whether it has moved yet */
  private pinDrag: { pointerId: number; dx: number; dy: number; startX: number; startY: number; moved: boolean } | null = null;

  /** The scrolling sheet area: its wheel and gesture listeners are non-passive, so a pinch there zooms the sheet only */
  @ViewChild('sheetScroll') set sheetScroll(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement ?? null;
    if (el === this.scrollEl) return;
    this.unbindZoom?.();
    this.unbindZoom = null;
    this.scrollEl = el;
    if (el) {
      this.unbindZoom = bindSheetZoomListeners(el, {
        wheel: e => this.onWheel(e),
        gesturestart: e => this.safariPinch.start(e, this.zoom),
        gesturechange: e => {
          const zoom = this.safariPinch.change(e);
          if (zoom !== null && isSafariGesture(e)) this.zoomAt(zoom, e.clientX, e.clientY);
        },
        gestureend: e => this.safariPinch.end(e),
      });
    }
  }
  @ViewChild('sheetImage') private sheetImage?: ElementRef<HTMLImageElement>;

  ngOnInit(): void {
    // Review M5: a query already loaded (the × loads '' at once) is not asked for again
    this.subs.add(this.search.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged()).subscribe(q => {
      if (q !== this.loadedQuery) {
        this.loadSheets(q);
      }
    }));
    this.loadSheets('');
    const linked = this.link?.sheet;
    if (linked?.revision_uuid) {
      this.selectedSheetUuid = linked.sheet_uuid;
      this.point = linked.point ? { x: Number(linked.point.x), y: Number(linked.point.y) } : null;
      this.loadRevision(linked.revision_uuid);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.unbindZoom?.();
  }

  get selectedSheet(): DrawingSheet | null {
    return this.sheets.find(sheet => sheet.uuid === this.selectedSheetUuid) || null;
  }

  get revisionTitle(): string {
    const sheet = this.revision?.sheet;
    if (!this.revision) return '';
    return [sheet?.number, this.revision.label ? `Rev ${this.revision.label}` : '', sheet?.title].filter(Boolean).join(' · ');
  }

  /** "Superseded" / "Withdrawn" when the record's revision is no longer current. */
  get revisionBadge(): string {
    if (this.revision?.status === 'withdrawn') return 'Withdrawn';
    if (this.revision?.status === 'superseded') return 'Superseded';
    return '';
  }

  sheetDetail(sheet: DrawingSheet): string {
    return [sheet.current?.label ? `Rev ${sheet.current.label}` : '', sheetStationText(sheet.station_start, sheet.station_end), sheet.title]
      .filter(Boolean).join(' · ');
  }

  onSearch(value: string): void {
    this.query = value;
    this.search.next(value.trim());
  }

  /** The search's ×: every sheet again, at once */
  clearSearch(): void {
    this.query = '';
    this.search.next('');
    this.loadSheets('');
  }

  /** Audit 1: more sheets than are listed (the register pages 100 at a time) */
  get hasMoreSheets(): boolean {
    return !!this.pageParams && this.nextStart < this.totalSheets;
  }

  private loadSheets(q: string): void {
    this.loadedQuery = q;
    const generation = ++this.listGeneration;
    const base: DrawingSheetListParams = { status: 'current', start: 0, total: SHEET_PAGE_SIZE, ...(q ? { q } : {}) };
    const station = !q && this.station !== null && this.station !== undefined && Number.isFinite(Number(this.station)) ? Number(this.station) : null;
    this.loadingSheets = true;
    this.listError = '';
    this.loadingMore = false;
    this.moreError = '';
    this.pageParams = null;
    const fetch = (params: DrawingSheetListParams, fallback: boolean) => {
      this.listPage(params).subscribe({
        next: ({ data, totalRecords }) => {
          if (generation !== this.listGeneration) return;
          const rows = (data || []).filter(sheet => !!sheet.current);
          if (!rows.length && fallback) { fetch(base, false); return; }
          this.nearStation = fallback;
          this.sheets = rows;
          this.pageParams = params;
          this.nextStart = (data || []).length;
          this.totalSheets = totalRecords ?? 0;
          this.loadingSheets = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          if (generation !== this.listGeneration) return;
          this.loadingSheets = false;
          this.listError = this.listFailure(error);
          this.cdr.markForCheck();
        },
      });
    };
    fetch(station !== null ? { ...base, station } : base, station !== null);
  }

  /** The first page failed: the same list again */
  retryList(): void {
    this.loadSheets(this.loadedQuery ?? '');
  }

  /** Audit 1: the next 100 (Show more, or scrolling near the end), appended as it arrives */
  loadMore(): void {
    const params = this.pageParams;
    if (!params || this.loadingMore || this.loadingSheets || !this.hasMoreSheets) return;
    const generation = this.listGeneration;
    this.loadingMore = true;
    this.moreError = '';
    this.listPage({ ...params, start: this.nextStart }).subscribe({
      next: ({ data, totalRecords }) => {
        if (generation !== this.listGeneration) return;
        const seen = new Set(this.sheets.map(sheet => sheet.uuid));
        this.sheets = [...this.sheets, ...(data || []).filter(sheet => !!sheet.current && !seen.has(sheet.uuid))];
        this.nextStart += (data || []).length;
        this.totalSheets = (data || []).length ? totalRecords ?? this.totalSheets : this.nextStart;
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        if (generation !== this.listGeneration) return;
        this.loadingMore = false;
        this.moreError = this.listFailure(error);
        this.cdr.markForCheck();
      },
    });
  }

  /** Scrolling the list near its end asks for the next page */
  onListScroll(event: Event): void {
    const list = event.target as HTMLElement;
    if (list && list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_MORE_THRESHOLD_PX && !this.moreError) {
      this.loadMore();
    }
  }

  private listPage(params: DrawingSheetListParams) {
    return this.drawings.listSheets(this.projectId, params).pipe(timeout(SHEET_LIST_TIMEOUT_MS));
  }

  /** Plain words only, never the server's text */
  private listFailure(error: unknown): string {
    if (error instanceof TimeoutError) return LIST_TIMED_OUT;
    return (error as { status?: unknown } | null)?.status === 0 ? OFFLINE_COPY : LIST_FAILED;
  }

  choose(sheet: DrawingSheet): void {
    if (!sheet.current?.uuid || (sheet.uuid === this.selectedSheetUuid && this.revision?.uuid === sheet.current.uuid)) return;
    this.selectedSheetUuid = sheet.uuid;
    this.point = null;
    this.zoom = 1;
    this.loadRevision(sheet.current.uuid);
  }

  private loadRevision(revisionUuid: string): void {
    const generation = ++this.revisionGeneration;
    this.revision = null;
    this.imageLoaded = false;
    this.loadingRevision = true;
    this.revisionError = '';
    this.drawings.getRevision(this.projectId, revisionUuid).subscribe({
      next: revision => {
        if (generation !== this.revisionGeneration) return;
        this.revision = revision;
        this.loadingRevision = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        if (generation !== this.revisionGeneration) return;
        this.loadingRevision = false;
        this.revisionError = this.drawings.errorMessage(error) || 'This sheet could not be opened. Try again.';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * The click in the page image's own box: the image fills its frame at the page's aspect (no object-fit), so offset
   * over the rendered size is the point in page space.
   */
  place(event: MouseEvent): void {
    // W75: the click that ends a pan (over 4px) places nothing
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.pinMenuOpen = false;
    const point = this.revision ? sheetPointFromClick(event) : null;
    if (!point) return;
    this.point = point;
    this.picked.emit({ revision: this.revision, sheet: this.selectedSheet, point: this.point });
    this.cdr.markForCheck();
  }

  /** Keyboard placement: Enter or Space puts the point at the centre, arrows move it 1% of the page. */
  onImageKey(event: KeyboardEvent): void {
    // Audit 2: Delete / Backspace on the sheet asks to remove the pin (the keyboard's Remove pin)
    if ((event.key === 'Delete' || event.key === 'Backspace') && this.point) {
      event.preventDefault();
      this.askRemovePin();
      return;
    }
    const steps: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = steps[event.key];
    if (!this.revision || !this.imageLoaded || (!step && event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const from = this.point ?? { x: 0.5, y: 0.5 };
    this.point = step && this.point ? { x: clamp(from.x + step[0] * 0.01), y: clamp(from.y + step[1] * 0.01) } : from;
    this.picked.emit({ revision: this.revision, sheet: this.selectedSheet, point: this.point });
    this.cdr.markForCheck();
  }

  /** The page image finished loading: clicks and keys may place the point. */
  onImageLoad(): void {
    this.imageLoaded = true;
    this.cdr.markForCheck();
  }

  /** The stack's + / −: the next step of the ladder, about the middle of what is in view (as the viewer, W66) */
  zoomBy(step: 1 | -1): void {
    const next = stepZoom(ZOOMS, this.zoom, step);
    const box = this.scrollEl?.getBoundingClientRect();
    if (box && this.imageLoaded) {
      this.zoomAt(next, box.left + this.scrollEl.clientWidth / 2, box.top + this.scrollEl.clientHeight / 2);
      return;
    }
    this.zoom = next;
    this.cdr.markForCheck();
  }

  /** Zoom to `zoom` (clamped to the ladder) keeping the page point under (clientX, clientY) where it is */
  zoomAt(zoom: number, clientX: number, clientY: number): void {
    const image = this.sheetImage?.nativeElement;
    const next = clampZoom(zoom, ZOOMS[0], ZOOMS[ZOOMS.length - 1]);
    if (!image || !this.scrollEl || !this.imageLoaded || Math.abs(next - this.zoom) < 1e-3) return;
    const before = image.getBoundingClientRect();
    const fx = pageFraction(clientX, before.left, before.width);
    const fy = pageFraction(clientY, before.top, before.height);
    this.zoom = next;
    this.cdr.detectChanges();
    const after = image.getBoundingClientRect();
    this.scrollEl.scrollLeft += anchorScrollDelta(fx, clientX, after.left, after.width);
    this.scrollEl.scrollTop += anchorScrollDelta(fy, clientY, after.top, after.height);
  }

  /** ctrl / ⌘ + wheel and the trackpad pinch zoom the sheet at the cursor (never the page); a plain wheel scrolls (pans) */
  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    if (this.safariPinch.active) return;
    this.zoomAt(this.zoom * wheelZoomFactor(event.deltaY), event.clientX, event.clientY);
  }

  /** A mouse or pen press on the sheet: a click under 4px places the point; past that it is a pan, and places nothing */
  onScrollPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    this.drag.down(event.clientX, event.clientY);
    this.dragPointer = event.pointerId;
  }

  onScrollPointerMove(event: PointerEvent): void {
    if (!this.drag.active) return;
    if (buttonsReleased(event)) {
      this.drag.cancel();
      return;
    }
    const { dx, dy } = this.drag.move(event.clientX, event.clientY);
    if (this.drag.dragging && this.dragPointer !== null) capturePointer(this.scrollEl, this.dragPointer);
    if (this.scrollEl && (dx || dy)) {
      this.scrollEl.scrollLeft += dx;
      this.scrollEl.scrollTop += dy;
    }
  }

  onScrollPointerUp(event: PointerEvent): void {
    if (event.pointerType === 'touch') return;
    releasePointer(this.scrollEl, event.pointerId);
    this.dragPointer = null;
    if (this.drag.up()) {
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false));
    }
  }

  onScrollCaptureLost(): void {
    if (this.drag.active) this.drag.cancel();
  }

  /** W75: the pin can be dragged to a new spot (as on mobile); the tip follows the pointer less the grab offset */
  onPinDown(event: PointerEvent): void {
    const image = this.sheetImage?.nativeElement;
    if (!image || !this.point || !this.revision || (event.pointerType !== 'touch' && event.button !== 0)) return;
    event.preventDefault();
    event.stopPropagation();
    const box = image.getBoundingClientRect();
    this.pinDrag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, moved: false,
      dx: event.clientX - (box.left + this.point.x * box.width), dy: event.clientY - (box.top + this.point.y * box.height) };
    capturePointer(event.currentTarget as Element, event.pointerId);
  }

  onPinMove(event: PointerEvent): void {
    const drag = this.pinDrag;
    const image = this.sheetImage?.nativeElement;
    if (!drag || !image || event.pointerId !== drag.pointerId) return;
    event.stopPropagation();
    if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <= DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    const box = image.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    this.point = { x: clamp((event.clientX - drag.dx - box.left) / box.width), y: clamp((event.clientY - drag.dy - box.top) / box.height) };
    this.cdr.markForCheck();
  }

  onPinUp(event: PointerEvent): void {
    const drag = this.pinDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    event.stopPropagation();
    releasePointer(event.currentTarget as Element, event.pointerId);
    this.pinDrag = null;
    if (drag.moved && this.revision && this.point) {
      this.picked.emit({ revision: this.revision, sheet: this.selectedSheet, point: this.point });
    } else if (!drag.moved) {
      // Audit 2: a click on the pin (no drag) opens its menu: Remove pin
      this.pinMenuOpen = !this.pinMenuOpen;
      this.cdr.markForCheck();
    }
  }

  /** Mark up the sheet on screen, pin or not (a markup-only location) */
  askMarkUp(): void {
    if (!this.revision || !this.imageLoaded || !this.canMarkUp) return;
    this.markUp.emit({ revision: this.revision, sheet: this.selectedSheet });
  }

  askRemovePin(): void {
    this.pinMenuOpen = false;
    this.removePin.emit();
  }

  /** The dialog removed the pin: the sheet waits for a new one */
  clearPoint(): void {
    this.point = null;
    this.pinMenuOpen = false;
    this.cdr.markForCheck();
  }

  get pinDragging(): boolean { return !!this.pinDrag?.moved; }
  /** A pan is under way (past the 4px threshold): grabbing over the whole sheet */
  get panning(): boolean { return this.drag.dragging; }

  get canZoomIn(): boolean { return this.zoom < ZOOMS[ZOOMS.length - 1]; }
  get canZoomOut(): boolean { return this.zoom > ZOOMS[0]; }
}
